import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  Box,
  ButtonBase,
  CircularProgress,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { AccountAvatar } from './AccountAvatar';
import { SwitcherEntry } from './accountSwitcherLabels';

const customStyles = {
  // `ButtonBase` and not a `Box` with `onClick`: the popover traps the Tab cycle,
  // so a row that is not focusable cannot be reached at all. Somebody could
  // filter the whole list by keyboard and not be able to pick what they found.
  // It also brings role, focus ring, Enter and Space for free.
  // The Tooltip wraps this span and not the button: a disabled `ButtonBase`
  // carries `pointer-events: none`, and the current account's row is disabled, so
  // it would be the only one without a tooltip.
  rowWrapper: {
    display: 'block',
    width: '100%',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
    gap: '10px',
    padding: '6px 10px',
    borderRadius: '8px',
    minWidth: 0,
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: (theme) => theme.palette.action.hover,
    },
  },
  rowCurrent: {
    backgroundColor: (theme) => theme.palette.action.hover,
  },
  rowSwitching: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  names: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: '14px',
    fontWeight: '400',
    minWidth: 0,
  },
  labelCurrent: {
    fontWeight: '500',
    color: (theme) => theme.palette.primary.main,
  },
  hint: {
    fontSize: '12px',
    color: '#6E7178',
    flexShrink: 0,
  },
  check: {
    fontSize: '18px',
    flexShrink: 0,
    color: (theme) => theme.palette.primary.main,
  },
  spinner: {
    width: '14px !important',
    height: '14px !important',
    flexShrink: 0,
  },
} satisfies Record<string, SxProps<Theme>>;

interface Props {
  entry: SwitcherEntry;
  isCurrent: boolean;
  /** True while this row's switch is in flight. */
  isSwitching: boolean;
  /** Read out on the check, which is the only marker the current row carries. */
  currentAccountLabel: string;
  avatarSize?: number;
  /** The label goes up with the id: whoever runs the switch needs the name to
   * show while it is in flight, and the row is where it is already resolved. */
  onSelect: (userId: number, accountName: string) => void;
}

/**
 * One account in the selector, in the menu and in the full list alike.
 *
 * The name truncates instead of wrapping so every row keeps the same height and
 * the list stays scannable. The tooltip carries the untruncated name AND the
 * account id: a name cut at "Constructora Los Aromos S…" no longer identifies
 * the account, and two accounts can share a name outright.
 *
 * The current row is not clickable: there is no switch to where you already are.
 */
export function AccountSwitcherRow({
  entry,
  isCurrent,
  isSwitching,
  currentAccountLabel,
  avatarSize,
  onSelect,
}: Props) {
  // `ID` is literal: it is the same word in all three languages, and passing it
  // as copy would mean threading another prop through this row's three callers.
  const tooltip = `${entry.label} · ID ${entry.account_id}`;

  return (
    <Tooltip
      title={tooltip}
      placement='top'
      enterDelay={400}
    >
      <Box
        component='span'
        sx={customStyles.rowWrapper}
      >
        <ButtonBase
          sx={[
            customStyles.row,
            isCurrent && customStyles.rowCurrent,
            isSwitching && customStyles.rowSwitching,
          ]}
          onClick={() => onSelect(entry.user_id, entry.label)}
          disabled={isCurrent || isSwitching}
          data-testid={`account-switcher-option-${entry.account_id}`}
        >
          <AccountAvatar
            accountId={entry.account_id}
            accountName={entry.label}
            size={avatarSize}
          />
          <Box sx={customStyles.names}>
            <Typography
              sx={[customStyles.label, isCurrent && customStyles.labelCurrent]}
              noWrap
            >
              {entry.label}
            </Typography>
            {entry.hint !== null && <Typography sx={customStyles.hint}>{entry.hint}</Typography>}
          </Box>
          {isSwitching && <CircularProgress sx={customStyles.spinner} />}
          {!isSwitching && isCurrent && (
            <CheckRoundedIcon
              sx={customStyles.check}
              titleAccess={currentAccountLabel}
              data-testid={`account-switcher-current-${entry.account_id}`}
            />
          )}
        </ButtonBase>
      </Box>
    </Tooltip>
  );
}
